import { Reveal } from "@/components/Reveal";
import { SunDoodle, WavyUnderline } from "@/components/Doodles";

/* Crayon-style doodles matching the site's hand-drawn SVG stroke style */
const RainbowDoodle = ({ className = "", size = 66 }) => (
    <svg width={size} height={size * 0.6} viewBox="0 0 66 40" fill="none" className={className} aria-hidden="true">
        {["#E07A5F", "#F6C544", "#8FB38A", "#A8D5E2"].map((c, i) => (
            <path
                key={c}
                d={`M${5 + i * 5} 36 A ${28 - i * 5} ${28 - i * 5} 0 0 1 ${61 - i * 5} 36`}
                stroke={c}
                strokeWidth="3.6"
                strokeLinecap="round"
            />
        ))}
    </svg>
);

const HeartDoodle = ({ className = "", size = 30 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
        <path
            d="M12 20.5C6.8 16.4 3.5 13.2 3.5 9.7 3.5 7.2 5.4 5.3 7.7 5.3c1.7 0 3.2 1 4.3 2.6 1.1-1.6 2.6-2.6 4.3-2.6 2.3 0 4.2 1.9 4.2 4.4 0 3.5-3.3 6.7-8.5 10.8z"
            fill="#E07A5F"
            stroke="#3D3229"
            strokeWidth="1.8"
            strokeLinejoin="round"
        />
    </svg>
);

const StarDoodle = ({ className = "", size = 26 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
        <path
            d="M12 2.8l2.5 5.9 6.4.5-4.9 4.2 1.5 6.3L12 16.3l-5.5 3.4 1.5-6.3-4.9-4.2 6.4-.5z"
            fill="#F6C544"
            stroke="#3D3229"
            strokeWidth="1.7"
            strokeLinejoin="round"
        />
    </svg>
);

/* Wobbly crayon-scribble blob drawn behind each artwork */
const ScribbleBlob = ({ color }) => (
    <span className="absolute -inset-4 sm:-inset-7 rotate-2" aria-hidden="true">
        <svg viewBox="0 0 220 190" preserveAspectRatio="none" className="w-full h-full">
            <path
                d="M110 14C168 8 210 42 206 96c-4 54-50 84-99 80S12 146 15 92C18 44 56 20 110 14z"
                fill={color}
                fillOpacity="0.2"
                stroke={color}
                strokeOpacity="0.65"
                strokeWidth="5.5"
                strokeLinecap="round"
            />
            <path
                d="M112 34c46-4 76 20 74 58-2 40-36 62-76 59-40-3-68-24-66-62 2-36 28-52 68-55z"
                fill="none"
                stroke={color}
                strokeOpacity="0.5"
                strokeWidth="4"
                strokeLinecap="round"
            />
        </svg>
    </span>
);

const ARTWORKS = [
    { id: "coco", src: "/images/cayden-coco.jpg", alt: "Cayden's drawing of Coco the dog next to a colorful crayon picture", color: "#F6C544", rotate: "-rotate-2", label: null, imgCls: "aspect-[4/3] w-full object-cover" },
    { id: "mom", src: "/images/cayden-mom.jpg", alt: "Cayden's marker portrait of his mom wearing glasses", color: "#8FB38A", rotate: "rotate-1", label: "Mom, by Cayden", imgCls: "aspect-[4/3] w-full object-cover" },
    { id: "cayden", src: "/images/cayden-cayden.jpg", alt: "Cayden smiling and proudly holding his artwork", color: "#A8D5E2", rotate: "rotate-2", label: null, imgCls: "aspect-square w-full object-cover object-[50%_40%]" },
    { id: "hearts", src: "/images/cayden-hearts.jpg", alt: "Cayden's drawing of two people surrounded by hearts, signed Cayden and Mom", color: "#E07A5F", rotate: "-rotate-1", label: null, imgCls: "aspect-[4/3] w-full object-cover" },
    { id: "house", src: "/images/cayden-house.jpg", alt: "Cayden's oil pastel picture of a house under a blue sky with the sun", color: "#8FB38A", rotate: "rotate-1", label: null, imgCls: "aspect-[4/3] w-full object-cover" },
];

export default function CaydenGallery() {
    return (
        <section className="mt-14 md:mt-20 relative" aria-label="Gallery of Cayden's artwork" data-testid="cayden-gallery-section">
            <SunDoodle className="absolute -top-4 right-2 lg:right-8 hidden sm:block" size={52} />
            <RainbowDoodle className="absolute top-24 -left-1 lg:left-4 hidden lg:block" />
            <StarDoodle className="absolute top-6 left-10 hidden md:block" size={24} />
            <HeartDoodle className="absolute bottom-40 -left-1 lg:left-8 hidden lg:block" size={28} />
            <StarDoodle className="absolute bottom-24 right-6 lg:right-16 hidden md:block" size={20} />

            <Reveal className="text-center max-w-2xl mx-auto">
                <h2 data-testid="cayden-gallery-title" className="font-display font-semibold text-2xl md:text-3xl">
                    Creativity Grows Here
                    <WavyUnderline className="block w-40 h-2.5 mt-2 mx-auto" color="#F6C544" />
                </h2>
                <p className="mt-5 text-lg text-soilmuted leading-relaxed">
                    At Little Seeds Flourish, creativity is encouraged, celebrated, and shared. Art gives children the
                    freedom to imagine, explore, and bring their own ideas to life.
                </p>
            </Reveal>

            <div className="mt-10 md:mt-14 flex flex-wrap justify-center gap-x-7 gap-y-10 md:gap-x-10">
                {ARTWORKS.map((a, i) => (
                    <Reveal key={a.id} delay={i * 0.07} className="w-[46%] sm:w-[42%] md:w-[29%]">
                        <figure
                            className={`relative ${a.rotate} transition-transform duration-300 hover:rotate-0 hover:scale-[1.03]`}
                            data-testid={`cayden-art-${a.id}`}
                        >
                            <ScribbleBlob color={a.color} />
                            <div className="relative bg-white p-2.5 pb-3 shadow-md border border-soil/10">
                                <img src={a.src} alt={a.alt} loading="lazy" className={a.imgCls} />
                                {a.label && (
                                    <figcaption
                                        className="mt-2 text-center font-display font-semibold text-sm md:text-base text-soil"
                                        data-testid="cayden-mom-label"
                                    >
                                        {a.label}
                                    </figcaption>
                                )}
                            </div>
                        </figure>
                    </Reveal>
                ))}
            </div>

            <Reveal delay={0.2}>
                <p className="mt-9 text-center text-sm font-bold text-soil/55" data-testid="cayden-gallery-caption">
                    Art by Cayden, Asia's son and our resident little artist 🎨
                </p>
            </Reveal>
        </section>
    );
}
